import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { createAgentToken, deleteAgentToken, listAgentTokens } from './api';

const COMMON_CHAINS = ['ethereum', 'base', 'arbitrum', 'optimism', 'polygon'];

export function PoliciesTokensPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const tokens = useQuery({
    queryKey: ['agent-tokens', orgId],
    queryFn: () => listAgentTokens(orgId),
    enabled: !!orgId,
  });

  const [chain, setChain] = useState('ethereum');
  const [address, setAddress] = useState('');
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [decimals, setDecimals] = useState(6);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createAgentToken(orgId, { chain, address, symbol, name, decimals, kind: 'erc20' }),
    onSuccess: async () => {
      setChain('ethereum');
      setAddress('');
      setSymbol('');
      setName('');
      setDecimals(6);
      await qc.invalidateQueries({ queryKey: ['agent-tokens', orgId] });
    },
    onError: (e: unknown) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAgentToken(orgId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-tokens', orgId] }),
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!chain || !address || !symbol) {
      setError('chain, address and symbol are required');
      return;
    }
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="What is the token registry?"
          description="The set of tokens the policy engine recognises when an agent calls sendTransaction. Sigil seeds the common stablecoins (USDC, USDT, EURC, EURT). Add your own here so the engine can apply token-level rules to them."
        />
      </Card>

      <Card>
        <CardHeader title="Add a custom token" />
        <CardBody>
          <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">Chain</label>
              <select
                className="mt-1 block h-10 w-full rounded-lg border border-line bg-surface-white px-3 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
                value={chain}
                onChange={(e) => setChain(e.target.value)}
              >
                {COMMON_CHAINS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <Input
              label="Symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="USDX"
              className="sm:col-span-1"
            />
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Stable"
              className="sm:col-span-2"
            />
            <Input
              label="Decimals"
              type="number"
              min={0}
              max={36}
              value={String(decimals)}
              onChange={(e) => setDecimals(parseInt(e.target.value, 10) || 0)}
              className="sm:col-span-1"
            />
            <Input
              label="Contract address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="0x..."
              className="sm:col-span-6 font-mono"
            />

            {error && (
              <div className="sm:col-span-6 rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}

            <div className="sm:col-span-6 flex justify-end">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Adding…' : 'Add token'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Tokens" description="Defaults (lock icon) come from Sigil and apply to every org. Customs you add are scoped to this organisation." />
        <CardBody className="p-0">
          {tokens.isLoading && <div className="p-6 text-sm text-ink-tertiary">Loading…</div>}
          {tokens.data && tokens.data.length === 0 && (
            <div className="p-6 text-sm text-ink-tertiary">No tokens.</div>
          )}
          {tokens.data && tokens.data.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line-soft text-xs uppercase tracking-eyebrow text-ink-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">Symbol</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Chain</th>
                  <th className="px-4 py-2 font-medium">Address</th>
                  <th className="px-4 py-2 font-medium">Decimals</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {tokens.data.map((t) => (
                  <tr key={t.id} className="border-t border-line-soft hover:bg-surface-soft/40">
                    <td className="px-4 py-2 font-medium text-ink-primary">{t.symbol}</td>
                    <td className="px-4 py-2 text-ink-secondary">{t.name}</td>
                    <td className="px-4 py-2 text-ink-secondary">{t.chain}</td>
                    <td className="px-4 py-2 font-mono text-xs text-ink-secondary">{shortAddr(t.address)}</td>
                    <td className="px-4 py-2 text-xs text-ink-secondary">{t.decimals}</td>
                    <td className="px-4 py-2 text-xs">
                      {t.is_default ? (
                        <span className="rounded bg-surface-soft px-1.5 py-0.5 font-mono text-[10px] uppercase text-ink-tertiary">
                          sigil
                        </span>
                      ) : (
                        <span className="rounded bg-bit2me/10 px-1.5 py-0.5 font-mono text-[10px] uppercase text-bit2me">
                          custom
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!t.is_default && (
                        <Button variant="ghost" onClick={() => remove.mutate(t.id)}>
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function shortAddr(a: string): string {
  return a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-4)}` : a;
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string; code?: string } } } }).response;
    if (resp?.data?.error) return resp.data.error.message ?? resp.data.error.code ?? 'unknown error';
  }
  return e instanceof Error ? e.message : 'unknown error';
}
